import { useState, useEffect, useCallback } from 'react';
import { clientInvoicesService } from '../lib/database';
import { getDataSource } from '../lib/config';
import type { ClientInvoice } from '../types';

interface UseClientInvoicesReturn {
  clientInvoices: ClientInvoice[];
  loading: boolean;
  error: string | null;
  create: (invoice: Omit<ClientInvoice, 'id'>) => Promise<ClientInvoice>;
  update: (id: string, updates: Partial<ClientInvoice>) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export const useClientInvoices = (enabled = true): UseClientInvoicesReturn => {
  const [clientInvoices, setClientInvoices] = useState<ClientInvoice[]>([]);
  const [loading, setLoading] = useState(() => enabled && getDataSource() !== 'mock');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (getDataSource() === 'mock') {
      setClientInvoices([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = clientInvoicesService.subscribe(
      (invoices) => {
        setClientInvoices(invoices);
        setLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err.message : 'Failed to load client invoices');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [enabled]);

  const create = useCallback(async (invoice: Omit<ClientInvoice, 'id'>) => {
    const created = await clientInvoicesService.create(invoice);
    setClientInvoices((prev) => [created, ...prev.filter((i) => i.id !== created.id)]);
    return created;
  }, []);

  const update = useCallback(async (id: string, updates: Partial<ClientInvoice>) => {
    await clientInvoicesService.update(id, updates);
    setClientInvoices((prev) =>
      prev.map((inv) => (inv.id === id ? { ...inv, ...updates } : inv)),
    );
  }, []);

  const remove = useCallback(async (id: string) => {
    await clientInvoicesService.delete(id);
    setClientInvoices((prev) => prev.filter((inv) => inv.id !== id));
  }, []);

  return { clientInvoices, loading, error, create, update, remove };
};
